<template>
  <div
    v-if="showBanner"
    class="cookie-banner"
    role="dialog"
    aria-modal="true"
    aria-labelledby="cookie-banner-title"
    aria-describedby="cookie-banner-desc">
    <div class="cookie-banner__inner">
      <div class="cookie-banner__content">
        <p
          id="cookie-banner-title"
          class="cookie-banner__title">
          {{ t('cookieConsent.title') }}
        </p>
        <p
          id="cookie-banner-desc"
          class="cookie-banner__desc">
          {{ t('cookieConsent.description') }}
          <NuxtLink
            :to="localePath('/politique-de-confidentialite')"
            class="cookie-banner__link">
            {{ t('cookieConsent.privacyLink') }}
          </NuxtLink>
        </p>
      </div>
      <div class="cookie-banner__actions">
        <button
          type="button"
          class="btn btn-ghost btn-sm cookie-banner__btn"
          @click="reject">
          {{ t('cookieConsent.reject') }}
        </button>
        <button
          type="button"
          class="btn btn-primary btn-sm cookie-banner__btn"
          @click="accept">
          {{ t('cookieConsent.accept') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { AnalyticsConsentComposableReturn } from '~/types/AnalyticsConsent'

const { t } = useI18n()
const localePath = useLocalePath()
const { showBanner, accept, reject }: AnalyticsConsentComposableReturn = useAnalyticsConsent()
</script>

<style scoped>
.cookie-banner {
  position: fixed;
  inset-inline: 0;
  bottom: 0;
  z-index: 600;
  padding: var(--space-4) var(--container-pad);
  padding-bottom: calc(var(--space-4) + env(safe-area-inset-bottom, 0px));
  pointer-events: none;
}

.cookie-banner__inner {
  pointer-events: auto;
  max-width: var(--container-lg);
  margin-inline: auto;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4) var(--space-6);
  padding: var(--space-5) var(--space-6);
  background-color: var(--bg-elevated);
  background-image: linear-gradient(135deg, var(--bg-surface) 0%, var(--bg-elevated) 100%);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
}

.cookie-banner__content {
  flex: 1 1 280px;
  min-width: 0;
}

.cookie-banner__title {
  margin: 0 0 var(--space-2);
  font-size: var(--text-base);
  font-weight: 700;
  color: var(--text-primary);
}

.cookie-banner__desc {
  margin: 0;
  font-size: var(--text-sm);
  line-height: var(--leading-relaxed);
  color: var(--text-secondary);
}

.cookie-banner__link {
  color: var(--accent-blue-lt);
  text-decoration: underline;
  text-underline-offset: 2px;
}

.cookie-banner__link:hover {
  color: var(--text-primary);
}

.cookie-banner__actions {
  display: flex;
  flex-shrink: 0;
  flex-wrap: wrap;
  gap: var(--space-2);
}

.cookie-banner__btn {
  white-space: nowrap;
}

@media (max-width: 640px) {
  .cookie-banner {
    padding: var(--space-3) var(--container-pad);
    padding-bottom: calc(var(--space-3) + env(safe-area-inset-bottom, 0px));
  }

  .cookie-banner__inner {
    flex-direction: column;
    align-items: stretch;
    gap: var(--space-3);
    padding: var(--space-4);
  }

  .cookie-banner__content {
    flex: 0 1 auto;
  }

  .cookie-banner__actions {
    flex-direction: column;
    gap: var(--space-3);
  }

  .cookie-banner__btn {
    width: 100%;
    justify-content: center;
    min-height: 2.75rem;
    font-size: var(--text-sm);
    padding: 0.75em 1.5em;
  }
}
</style>
